import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { get } from "lodash";

import { BooleanText, ErrorObject, Nullable } from "./api/types";
import {
    DataInputFormField,
    InputSingleValue,
    InputTypes,
    RPEFormField,
    SelectOption,
    TestGraph,
    TestSearchResult,
    TestSearchResultFromAPI,
    UpdateGraphData,
} from "./types";
import { fetcher } from "./api/utils";
import { TEST_ENDPOINT } from "./api/endpoints";

export const generateSelectOptions = ({
    enum: enumType,
    texts,
}: {
    enum: any;
    texts: any;
}): {
    id: any;
    value: any;
    label: string;
}[] => {
    return Object.values(enumType).map((value: any) => {
        return {
            id: value,
            value: value,
            label: texts[value],
        };
    });
};

export const getOptionValue = (option?: SelectOption) =>
    option === undefined || option === null
        ? null
        : typeof option === "string"
        ? option
        : option.value;
export const getOptionString = (option?: SelectOption) =>
    option === undefined || option === null
        ? null
        : typeof option === "string"
        ? option
        : option.label;

export function getErrorMessage(value: any): null | string {
    if (!value || typeof value !== "object") return null;
    let message: string | null =
        get(value, "error_message") || get(value, "error") || null;
    if (message) return message;

    Object.values(value).forEach((nestedValue) => {
        if (
            nestedValue &&
            typeof nestedValue === "object" &&
            get(nestedValue, "error_message") !== undefined
        ) {
            message = get(nestedValue, "error_message") || null;
        }
    });
    return message;
}

export function isRPEFormField(field: any): field is RPEFormField {
    return (
        (field as RPEFormField).scale !== undefined &&
        (field as RPEFormField).value !== undefined
    );
}

export function onFormValuesChange({
    value,
    fieldSettings,
    setFormValues,
    customData = {},
    valueAsNumber = false,
    noZeroValues = false,
}: {
    value: Nullable<string | number | BooleanText>;
    fieldSettings: DataInputFormField;
    setFormValues: React.Dispatch<React.SetStateAction<Nullable<any>>>;
    customData?: Object;
    valueAsNumber?: boolean;
    noZeroValues?: boolean;
}) {
    setFormValues((prevFormValues: Nullable<any>) => {
        if (!prevFormValues) {
            return prevFormValues;
        }
        const isMultipleSelect =
            fieldSettings.type === InputTypes.SELECT && fieldSettings.multiple;

        // calculate newValue
        let newValue;
        if (!isMultipleSelect) {
            newValue =
                noZeroValues && Number(value) === 0
                    ? null
                    : valueAsNumber
                    ? Number(value)
                    : value;
        } else {
            const currentArray: Array<InputSingleValue> =
                prevFormValues[fieldSettings.id] || [];
            const alreadySelected = currentArray.includes(
                value as InputSingleValue
            );

            const genericValues = [
                "None",
                "Normal",
                "Not determined",
                "Unknown",
            ];
            newValue = alreadySelected
                ? currentArray.filter((v) => v !== value)
                : genericValues.includes(value as any)
                ? [value]
                : [
                      ...currentArray.filter(
                          (v) => !genericValues.includes(v as any)
                      ),
                      value,
                  ];
        }
        return {
            ...prevFormValues,
            [fieldSettings.id]: newValue,
            ...customData,
        };
    });
}

export const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
    });
};

export const convertSearchResultsFromAPI = (
    results: TestSearchResultFromAPI[]
): TestSearchResult[] => {
    return results.map((result) => ({
        firstName: result.first_name,
        lastName: result.last_name,
        personId: result.person_id,
        status: result.test_status,
        testDate: new Date(result.test_date),
        testId: result.test_id,
    }));
};

export const toSnakeCase = (str: string) =>
    str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
export const toKebabCase = (str: string) =>
    str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

export const cn = (...inputs: ClassValue[]) => {
    return twMerge(clsx(inputs));
};

/**
 * Calculates the difference between two arrays of TestGraph objects.
 * compares only editable values.
 * if there is a difference, it returns the diff object.
 * if there is no difference, it returns null.
 * @param originalGraph - The original TestGraph object.
 * @param newGraph - The new TestGraph object.
 * @returns {UpdateGraphData | null} - The diff object or null if there is no difference.
 */
export const calcEditableValuesGraphsDiff = (
    originalGraph: TestGraph,
    newGraph: TestGraph
): UpdateGraphData | null => {
    const diff: UpdateGraphData = {};
    const editableKeys: (keyof TestGraph)[] = [
        "vat_value",
        "rcp_value",
        "slope_calculation_begin_value",
        "slope_calculation_end_value",
        "peak1_status",
        "peak2_status",
        "peak3_status",
        "response1_status",
        "response2_status",
        "slope1_status",
        "slope2_status",
        "slope3_status",
    ];

    for (const key of editableKeys) {
        if (originalGraph[key] !== newGraph[key]) {
            (diff as any)[key] = newGraph[key];
        }
    }

    return Object.keys(diff).length > 0 ? diff : null;
};

/**
 * Creates a digest (hash) from a file in the browser
 * @param {File} file - The file object from an input element or drag-and-drop
 * @param {string} algorithm - Hash algorithm to use (SHA-256, SHA-384, SHA-512, etc.)
 * @returns {Promise<string>} - Promise resolving to the hex digest
 */
export async function createFileDigest(
    file: File,
    algorithm: "SHA-256" | "SHA-384" | "SHA-512" = "SHA-256"
): Promise<string> {
    const secretKey = await getDigestSecretKey();
    const key = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(secretKey),
        { name: "HMAC", hash: algorithm },
        false,
        ["sign"]
    );

    const fileBuffer = await file.arrayBuffer();

    const signature = await crypto.subtle.sign("HMAC", key, fileBuffer);
    const signatureArray = Array.from(new Uint8Array(signature));
    const hexHMAC = signatureArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return hexHMAC;
}

export const getDigestSecretKey = async (): Promise<string> => {
    const response = await fetcher(
        `${TEST_ENDPOINT}/digest-key`,
        { method: "GET" },
        true
    );
    if (response.error) {
        throw new Error(response.error);
    }

    return response.digest_key;
};

export function measureTextWidth(
    text: string,
    fontSize = 12,
    font: string = "Arial"
): number {
    const canvas = getCanvas();
    const context = canvas.getContext("2d");
    if (!context) return 0;
    context.font = `${fontSize}px ${font}`;
    const metrics = context.measureText(text);
    return metrics.width;

    function getCanvas() {
        const canvasId = "text-measure-canvas";
        const existingCanvas = document.querySelector<HTMLCanvasElement>(
            `#${canvasId}`
        );
        if (existingCanvas) return existingCanvas;
        const canvas = document.createElement("canvas");
        canvas.id = canvasId;
        return canvas;
    }
}

export const isO2PulseGraph = (graph: TestGraph) =>
    graph.response1_y_label.includes("O₂Pulse");
